import { useState, useEffect, useRef } from "react";
import { EventFormProps } from "@ilamy/calendar";
import { Dayjs } from "dayjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { ClientPicker } from "@/components/ClientPicker";
import { ServicePicker } from "@/components/ServicePicker";
import { X, Clock, ChevronDown } from "lucide-react";
import type {
  CalendarBooking,
  CalendarStaff,
  CalendarService,
  CalendarSpace,
  CalendarClient,
  ClientPackage,
} from "@/types";
import {
  shouldClearUnavailableStaff,
  visibleStaffForService,
} from "./staffSelection";
import { requiredBookingErrors } from "./bookingFormValidation";
import { durationMinutesFromRange, selectedCalendarService } from "./selectedService";

interface BusinessHoursRange {
  startHour: number;
  endHour: number;
}

interface CalendarBusiness {
  id: number;
  name: string;
}

interface BookingFormProps extends EventFormProps {
  orgSlug: string;
  staff: CalendarStaff[];
  services: CalendarService[];
  spaces: CalendarSpace[];
  businesses?: CalendarBusiness[];
  currentBusinessId?: number | null;
  businessHours?: BusinessHoursRange | null;
  onBookingCreated: (booking: CalendarBooking) => void;
  onBookingUpdated: (booking: CalendarBooking) => void;
  currentStaffId?: number | null;
  canReassignStaff?: boolean;
}

function extractDate(date: Dayjs | Date | string): string {
  if (typeof date === "object" && "format" in date) {
    return (date as Dayjs).format("YYYY-MM-DD");
  }
  // Parse and use local date
  const d = new Date(date as string | Date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function extractTime(date: Dayjs | Date | string): string {
  if (typeof date === "object" && "format" in date) {
    return (date as Dayjs).format("HH:mm");
  }
  // Parse and use local time
  const d = new Date(date as string | Date);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function formatDisplayTime(timeStr: string): string {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

function addMinutesToTime(timeStr: string, minutes: number): string {
  if (!timeStr) return "";
  const [hours, mins] = timeStr.split(":").map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;
  return `${newHours.toString().padStart(2, "0")}:${newMins.toString().padStart(2, "0")}`;
}

// Generate time slots in 15-minute intervals
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      slots.push(
        `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`,
      );
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

function packageSchedulingPrefill() {
  if (typeof window === "undefined") {
    return { clientId: "", serviceId: "", clientPackageId: "" };
  }

  const query = new URLSearchParams(window.location.search);
  return {
    clientId: query.get("client_id") || "",
    serviceId: query.get("service_id") || "",
    clientPackageId: query.get("client_package_id") || "",
  };
}

// Time Picker Component
function TimePicker({
  value,
  onChange,
  id,
  selectedDate,
  businessHours,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  selectedDate?: string;
  businessHours?: BusinessHoursRange | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter time slots based on business hours and past times
  const filteredSlots = TIME_SLOTS.filter((slot) => {
    const [hours, minutes] = slot.split(":").map(Number);
    const slotMinutes = hours * 60 + minutes;

    if (businessHours) {
      const minMinutes = Math.max(0, businessHours.startHour * 60);
      const maxMinutes = Math.min(24 * 60, businessHours.endHour * 60);
      if (slotMinutes < minMinutes || slotMinutes >= maxMinutes) return false;
    }

    // Filter out past times for today
    if (selectedDate) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      if (selectedDate === todayStr) {
        const now = today.getHours() * 60 + today.getMinutes();
        const [slotHours, slotMins] = slot.split(":").map(Number);
        const slotTime = slotHours * 60 + slotMins;
        if (slotTime <= now) return false;
      }
    }

    // Filter by search
    if (!search) return true;
    const display = formatDisplayTime(slot).toLowerCase();
    return display.includes(search.toLowerCase()) || slot.includes(search);
  });

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>{value ? formatDisplayTime(value) : "Seleccionar..."}</span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <div className="p-2">
            <Input
              placeholder="Buscar hora..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredSlots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => {
                  onChange(slot);
                  setIsOpen(false);
                  setSearch("");
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-accent ${
                  value === slot ? "bg-accent" : ""
                }`}
              >
                {formatDisplayTime(slot)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function BookingForm({
  open,
  selectedEvent,
  onDelete,
  onClose,
  orgSlug,
  staff,
  services,
  spaces,
  businesses = [],
  currentBusinessId,
  businessHours,
  onBookingCreated,
  onBookingUpdated,
  currentStaffId,
  canReassignStaff = true,
}: BookingFormProps) {
  const showBusinessPicker = businesses.length > 1;
  const isEditing =
    !!selectedEvent?.id && !selectedEvent.id.toString().startsWith("temp-");
  const existingData = selectedEvent?.data as CalendarBooking | undefined;

  const defaultStaffId = !canReassignStaff && currentStaffId
    ? currentStaffId.toString()
    : staff.length === 1 ? staff[0].id.toString() : "";
  const packagePrefill = useRef(packageSchedulingPrefill());

  const [formData, setFormData] = useState({
    business_id: currentBusinessId?.toString() || "",
    client_id: packagePrefill.current.clientId,
    service_id: packagePrefill.current.serviceId,
    space_id: "",
    staff_assignment_id: defaultStaffId,
    date: "",
    start_time: "",
    notes: "",
    client_package_id: packagePrefill.current.clientPackageId,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableStaffIds, setAvailableStaffIds] = useState<number[]>(
    staff.map((s) => s.id),
  );
  const [eligiblePackages, setEligiblePackages] = useState<ClientPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedClient, setSelectedClient] = useState<CalendarClient | null>(
    existingData?.client_id
      ? { id: existingData.client_id, name: existingData.client_name || "", phone: "" }
      : null
  );

  useEffect(() => {
    const clientId = packagePrefill.current.clientId
    if (!clientId) return
    fetch(`/${orgSlug}/clients/${clientId}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const client = payload?.client
        if (!client) return
        setSelectedClient({ id: client.id, name: client.name, phone: client.phone })
      })
      .catch(() => undefined)
  }, [orgSlug]);
  const idempotencyKey = useRef(
    `dashboard-package-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  // Reset form when selectedEvent changes (for both new and edit)
  useEffect(() => {
    if (!open) return;

    if (existingData) {
      // Editing existing booking
      setSelectedClient(
        existingData.client_id
          ? { id: existingData.client_id, name: existingData.client_name || "", phone: "" }
          : null
      );
      setFormData({
        business_id: currentBusinessId?.toString() || "",
        client_id: existingData.client_id?.toString() || "",
        service_id: existingData.service_id?.toString() || "",
        space_id: existingData.space_id?.toString() || "",
        staff_assignment_id:
          existingData.staff_assignment_id?.toString() ||
          (staff.length === 1 ? staff[0].id.toString() : ""),
        date: existingData.start ? extractDate(existingData.start) : "",
        start_time: existingData.start ? extractTime(existingData.start) : "",
        notes: existingData.notes || "",
        client_package_id: existingData.client_package_id?.toString() || "",
      });
    } else if (selectedEvent?.start) {
      // New booking from calendar click
      setFormData({
        business_id: currentBusinessId?.toString() || "",
        client_id: packagePrefill.current.clientId,
        service_id: packagePrefill.current.serviceId,
        space_id: "",
        staff_assignment_id: defaultStaffId,
        date: extractDate(selectedEvent.start),
        start_time: extractTime(selectedEvent.start),
        notes: "",
        client_package_id: packagePrefill.current.clientPackageId,
      });
    }
  }, [open, selectedEvent, existingData, staff]);

  useEffect(() => {
    if (!open || isEditing || !formData.client_id) {
      setEligiblePackages([]);
      return;
    }

    const loadPackages = async () => {
      setLoadingPackages(true);
      try {
        const query = new URLSearchParams({
          client_id: formData.client_id,
        });
        if (formData.business_id) {
          query.set("business_id", formData.business_id);
        }
        const response = await fetch(
          `/${orgSlug}/calendar/eligible_packages?${query.toString()}`,
        );
        if (!response.ok) return;
        const payload = await response.json();
        setEligiblePackages(payload.client_packages || []);
      } finally {
        setLoadingPackages(false);
      }
    };

    loadPackages();
  }, [
    open,
    isEditing,
    formData.client_id,
    formData.business_id,
    orgSlug,
  ]);

  // Check staff availability when date/time/service changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!formData.date || !formData.start_time || !formData.service_id) {
        setAvailableStaffIds(staff.map((s) => s.id));
        return;
      }

      const selectedService = services.find(
        (s) => s.id.toString() === formData.service_id,
      );
      if (!selectedService) return;

      const localDate = new Date(`${formData.date}T${formData.start_time}:00`);
      const startsAt = localDate.toISOString();
      const query = new URLSearchParams({
        starts_at: startsAt,
        duration_minutes: String(selectedService.duration_minutes),
        service_id: formData.service_id,
      });
      if (existingData?.id) {
        query.set("exclude_booking_id", existingData.id);
      }

      try {
        const response = await fetch(
          `/${orgSlug}/calendar/staff_availability?${query.toString()}`,
        );
        if (response.ok) {
          const data = await response.json();
          const availableIds = Array.isArray(data.available_staff_ids)
            ? data.available_staff_ids.map(Number)
            : [];
          if (
            existingData?.staff_assignment_id &&
            !availableIds.includes(existingData.staff_assignment_id)
          ) {
            availableIds.push(existingData.staff_assignment_id);
          }
          setAvailableStaffIds(availableIds);

          if (
            shouldClearUnavailableStaff({
              canReassignStaff,
              selectedStaffId: formData.staff_assignment_id,
              availableStaffIds: availableIds,
              keepStaffId: existingData?.staff_assignment_id,
            })
          ) {
            setFormData((prev) => ({ ...prev, staff_assignment_id: "" }));
          }
        }
      } catch (error) {
        console.error("Failed to check staff availability:", error);
      }
    };

    checkAvailability();
  }, [
    formData.date,
    formData.start_time,
    formData.service_id,
    orgSlug,
    services,
    staff,
    canReassignStaff,
    existingData?.id,
    existingData?.staff_assignment_id,
  ]);

  useEffect(() => {
    if (!open) return;

    const selected = services.find(
      (service) => service.id.toString() === formData.service_id,
    );
    const visible = visibleStaffForService({
      staff,
      eligibleStaffIds: selected?.staff_assignment_ids,
      keepStaffId: existingData?.staff_assignment_id,
    });

    if (visible.length === 1) {
      const onlyId = String(visible[0].id);
      setFormData((prev) =>
        prev.staff_assignment_id === onlyId
          ? prev
          : { ...prev, staff_assignment_id: onlyId },
      );
      return;
    }

    if (
      canReassignStaff &&
      formData.staff_assignment_id &&
      !visible.some(
        (member) => String(member.id) === formData.staff_assignment_id,
      )
    ) {
      setFormData((prev) => ({ ...prev, staff_assignment_id: "" }));
    }
  }, [
    open,
    formData.service_id,
    formData.staff_assignment_id,
    services,
    staff,
    canReassignStaff,
    existingData?.staff_assignment_id,
  ]);

  useEffect(() => {
    if (!open || isEditing || !formData.space_id) return;

    const selected = services.find(
      (service) => service.id.toString() === formData.service_id,
    );
    const allowedIds = selected?.space_ids ?? [];
    const selectedSpace = spaces.find(
      (space) => space.id.toString() === formData.space_id,
    );
    const remainsEligible =
      selectedSpace?.active &&
      (allowedIds.length === 0 || allowedIds.includes(selectedSpace.id));

    if (!remainsEligible) {
      setFormData((prev) => ({ ...prev, space_id: "" }));
    }
  }, [open, isEditing, formData.service_id, formData.space_id, services, spaces]);

  if (!open) return null;

  const selectedService = selectedCalendarService({
    services,
    serviceId: formData.service_id,
    fallback: existingData
      ? {
          id: existingData.service_id,
          name: existingData.service_name,
          emoji: existingData.service_emoji,
          duration_minutes: durationMinutesFromRange(
            existingData.start,
            existingData.end,
          ),
        }
      : null,
  });
  const visibleStaff = visibleStaffForService({
    staff,
    eligibleStaffIds: selectedService?.staff_assignment_ids,
    keepStaffId: existingData?.staff_assignment_id,
  });
  const eligibleSpaces = selectedService
    ? spaces.filter(
        (space) =>
          space.active &&
          (!selectedService.space_ids?.length ||
            selectedService.space_ids.includes(space.id)),
      )
    : [];
  const endTime =
    formData.start_time && selectedService
      ? addMinutesToTime(formData.start_time, selectedService.duration_minutes)
      : "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const requiredErrors = requiredBookingErrors({
      clientId: formData.client_id,
      serviceId: formData.service_id,
    });
    if (Object.keys(requiredErrors).length > 0) {
      setErrors(requiredErrors);
      return;
    }

    setSaving(true);
    setErrors({});

    // Combine date and time into ISO string with timezone
    let startsAt = "";
    if (formData.date && formData.start_time) {
      // Create a local Date object and convert to ISO (UTC)
      const localDate = new Date(`${formData.date}T${formData.start_time}:00`);
      startsAt = localDate.toISOString();
    }

    const url = isEditing
      ? `/${orgSlug}/calendar/bookings/${selectedEvent?.id}`
      : `/${orgSlug}/calendar/bookings`;

    const method = isEditing ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          booking: {
            business_id: formData.business_id || null,
            client_id: formData.client_id || null,
            service_id: formData.service_id || null,
            space_id: formData.space_id || null,
            staff_assignment_id: formData.staff_assignment_id || null,
            starts_at: startsAt,
            notes: formData.notes,
            client_package_id: formData.client_package_id || null,
            idempotency_key: formData.client_package_id
              ? idempotencyKey.current
              : null,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (isEditing) {
          onBookingUpdated(data.booking);
        } else {
          onBookingCreated(data.booking);
        }
        if (formData.client_package_id && typeof window !== "undefined") {
          const url = new URL(window.location.href);
          url.searchParams.delete("client_id");
          url.searchParams.delete("service_id");
          url.searchParams.delete("client_package_id");
          window.history.replaceState({}, "", url);
          packagePrefill.current = {
            clientId: "",
            serviceId: "",
            clientPackageId: "",
          };
        }
        onClose();
      } else {
        const data = await response.json();
        setErrors(data.errors || {});
      }
    } catch (error) {
      console.error("Failed to save booking:", error);
    } finally {
      setSaving(false);
    }
  };

  const isPastBooking = existingData && new Date(existingData.end) < new Date();
  const isNoShow = existingData?.status === "no_show";
  const canMarkNoShow = isPastBooking && existingData?.status === "confirmed";

  const handleMarkNoShow = async () => {
    if (
      !selectedEvent?.id ||
      !confirm(`¿Marcar a "${existingData?.client_name}" como no show?`)
    )
      return;

    try {
      const response = await fetch(
        `/${orgSlug}/calendar/bookings/${selectedEvent.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || "",
          },
          body: JSON.stringify({ booking: { status: "no_show" } }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        onBookingUpdated(data.booking);
        onClose();
      }
    } catch (error) {
      console.error("Failed to mark no-show:", error);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?.id || !confirm("¿Eliminar esta cita?")) return;

    try {
      await fetch(`/${orgSlug}/calendar/bookings/${selectedEvent.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
      });
      onDelete?.(selectedEvent);
      onClose();
    } catch (error) {
      console.error("Failed to delete booking:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-xl border border-border bg-card p-4 shadow-lg sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar cita" : "Nueva cita"}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Sucursal picker — only when >1 business */}
          {showBusinessPicker && (
            <div className="space-y-2">
              <Label htmlFor="business">Sucursal</Label>
              <select
                id="business"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.business_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    business_id: e.target.value,
                    client_package_id: "",
                  }))
                }
              >
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <ClientPicker
            orgSlug={orgSlug}
            value={selectedClient}
            disabled={
              !!formData.client_package_id ||
              !!existingData?.covered_by_package
            }
            error={errors.client_id}
            onChange={(client) => {
              setSelectedClient(client)
              setFormData((prev) => ({
                ...prev,
                client_id: client ? String(client.id) : "",
                client_package_id: "",
              }))
            }}
          />

          <ServicePicker
            services={services}
            value={selectedService}
            disabled={
              !!formData.client_package_id ||
              !!existingData?.covered_by_package
            }
            error={errors.service_id}
            onChange={(service) =>
              setFormData((prev) => ({
                ...prev,
                service_id: service ? String(service.id) : "",
              }))
            }
          />

          {(eligibleSpaces.length > 0 || formData.space_id) && (
            <div className="space-y-2">
              <Label htmlFor="space">Espacio (opcional)</Label>
              <select
                id="space"
                className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                value={formData.space_id}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    space_id: event.target.value,
                  }))
                }
              >
                <option value="">Sin espacio</option>
                {formData.space_id &&
                  !eligibleSpaces.some((space) => String(space.id) === formData.space_id) && (
                    <option value={formData.space_id} disabled>
                      {existingData?.space_name || "Espacio no disponible"}
                    </option>
                  )}
                {eligibleSpaces.map((space) => (
                  <option key={space.id} value={space.id}>
                    {space.name}
                  </option>
                ))}
              </select>
              {errors.space && (
                <p className="text-sm text-destructive">{errors.space}</p>
              )}
            </div>
          )}

          {/* Staff assignment: 0-1 eligible staff = auto-assign, 2+ = show picker */}
          {visibleStaff.length <= 1 ? (
            <div className="space-y-2">
              <Label>Asignado a</Label>
              <div className="flex h-10 items-center rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground">
                {visibleStaff.length === 1 ? visibleStaff[0].name : "Propietario"}
              </div>
              {errors.staff_assignment && (
                <p className="text-sm text-destructive">
                  {errors.staff_assignment}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="staff">Asignar a</Label>
              <select
                id="staff"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                value={formData.staff_assignment_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    staff_assignment_id: e.target.value,
                  }))
                }
                required
                disabled={
                  !canReassignStaff || !formData.date || !formData.start_time || !formData.service_id
                }
              >
                <option value="">
                  {!formData.date ||
                  !formData.start_time ||
                  !formData.service_id
                    ? "Selecciona fecha, hora y servicio"
                    : "Sin asignar"}
                </option>
                {visibleStaff.map((s) => {
                  const isAvailable = availableStaffIds.includes(s.id);
                  return (
                    <option key={s.id} value={s.id} disabled={!isAvailable}>
                      {s.name}
                      {!isAvailable ? " (no disponible)" : ""}
                    </option>
                  );
                })}
              </select>
              {errors.staff_assignment && (
                <p className="text-sm text-destructive">
                  {errors.staff_assignment}
                </p>
              )}
            </div>
          )}

          {/* Date and Time row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Fecha</Label>
              <DatePicker
                date={
                  formData.date
                    ? new Date(formData.date + "T00:00:00")
                    : undefined
                }
                onSelect={(date) => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    setFormData((prev) => ({
                      ...prev,
                      date: `${year}-${month}-${day}`,
                    }));
                  } else {
                    setFormData((prev) => ({ ...prev, date: "" }));
                  }
                }}
                placeholder="Seleccionar fecha"
              />
              {errors.date && (
                <p className="text-sm text-destructive">{errors.date}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_time">Hora inicio</Label>
              <TimePicker
                id="start_time"
                value={formData.start_time}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, start_time: value }))
                }
                selectedDate={formData.date}
                businessHours={businessHours}
              />
              {errors.start_time && (
                <p className="text-sm text-destructive">{errors.start_time}</p>
              )}
            </div>
          </div>

          {!isEditing && formData.client_id && (
            <div className="space-y-2">
              <Label htmlFor="client_package">Cobertura</Label>
              <select
                id="client_package"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.client_package_id}
                onChange={(event) => {
                  const selectedId = event.target.value;
                  const selectedPackage = eligiblePackages.find(
                    (item) => item.id.toString() === selectedId,
                  );
                  setFormData((current) => ({
                    ...current,
                    client_package_id: selectedId,
                    service_id: selectedPackage
                      ? selectedPackage.service_id.toString()
                      : current.service_id,
                  }));
                }}
              >
                <option value="">
                  {loadingPackages
                    ? "Buscando paquetes..."
                    : "Cita normal (sin usar paquete)"}
                </option>
                {eligiblePackages.map((clientPackage) => (
                  <option key={clientPackage.id} value={clientPackage.id}>
                    {clientPackage.name} · {clientPackage.service_name} ·{" "}
                    {clientPackage.sessions_available} disponibles
                  </option>
                ))}
              </select>
              {formData.client_package_id && (
                <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
                  Se reservará una sesión del paquete. No se cobrará de nuevo
                  esta cita.
                </p>
              )}
              {errors.client_package && (
                <p className="text-sm text-destructive">
                  {errors.client_package}
                </p>
              )}
            </div>
          )}

          {isEditing && existingData?.covered_by_package && (
            <div className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
              Cubierta por {existingData.package_name || "paquete"} ·{" "}
              {existingData.package_sessions_available} sesiones disponibles
            </div>
          )}

          {/* End time display */}
          {endTime && selectedService && (
            <div className="space-y-2">
              <Label>Hora fin</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 pl-10 pr-3 text-sm text-muted-foreground">
                  {formatDisplayTime(endTime)}
                  <span className="ml-auto text-xs">
                    ({selectedService.duration_minutes} min)
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notas (opcional)</Label>
            <textarea
              id="notes"
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Notas adicionales..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </div>

          {isNoShow && (
            <div className="flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <X className="h-4 w-4 flex-shrink-0" />
              No show
            </div>
          )}

          {canMarkNoShow && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-destructive text-destructive hover:bg-destructive/10"
              onClick={handleMarkNoShow}
            >
              Marcar como no show
            </Button>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving
                ? "Guardando..."
                : isEditing
                  ? "Guardar cambios"
                  : "Crear cita"}
            </Button>
            {isEditing && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
              >
                Eliminar
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
